import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Check, Plus, EyeOff, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { formatoPorcentaje } from '@/lib/utils/formato-precio';
import {
  buscarArticulos,
  type RenglonDelPanel,
  type DecisionDeFila,
  type EstadoDeRenglon,
  type ArticuloDeTienda,
} from '../../services/carga-manual-service';

/**
 * Un renglón del panel de identificación (spec 199 §4.1).
 *
 * ⚖️ El selector vive DENTRO de la fila, no en una ventana aparte: en una lista
 * de 180, un modal te hace perder dónde estabas cada vez. A la izquierda lo que
 * se subió, a la derecha el candidato, y la fila se despliega en el lugar.
 */

const ETIQUETA_ESTADO: Record<EstadoDeRenglon, { texto: string; clase: string }> = {
  RECONOCIDO: { texto: 'Reconocido', clase: 'bg-green-100 text-green-800 hover:bg-green-100' },
  SIN_DECIDIR: { texto: 'Sin decidir', clase: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100' },
  NUEVO_MARCADO: { texto: 'Nuevo', clase: 'bg-blue-100 text-blue-800 hover:bg-blue-100' },
  RECHAZADO: { texto: 'Rechazado', clase: 'bg-red-100 text-red-800 hover:bg-red-100' },
};

/** Un salto de este tamaño casi nunca es un cambio de precio: es un error de lectura. */
const VARIACION_SOSPECHOSA = 100;

interface FilaRenglonProps {
  renglon: RenglonDelPanel;
  storeId: string;
  nombreTienda: string;
  onDecidir: (decision: DecisionDeFila) => void;
}

export function FilaRenglon({ renglon, storeId, nombreTienda, onDecidir }: FilaRenglonProps) {
  const [abierta, setAbierta] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [encontrados, setEncontrados] = useState<ArticuloDeTienda[] | null>(null);
  const [buscando, setBuscando] = useState(false);

  const estado = ETIQUETA_ESTADO[renglon.estado ?? 'SIN_DECIDIR'];
  const mejor = renglon.candidatos[0];
  const yaDecidido = renglon.estado === 'RECONOCIDO' || renglon.estado === 'NUEVO_MARCADO';

  /**
   * 🔴 El buscador va contra TODO el catálogo de la tienda, no contra los 5
   * candidatos que el puntaje eligió. Antes filtraba esos 5, así que un
   * artículo con 15 % —o el sexto mejor— era imposible de elegir: la decisión
   * de una persona quedaba limitada por nuestro puntaje.
   */
  useEffect(() => {
    const texto = busqueda.trim();
    if (texto.length < 2) {
      setEncontrados(null);
      return;
    }
    let vigente = true;
    setBuscando(true);
    // Medio segundo de espera: sin esto se dispara una consulta por tecla.
    const t = setTimeout(() => {
      buscarArticulos(storeId, texto, renglon.nombre)
        .then((r) => vigente && setEncontrados(r))
        .catch(() => vigente && setEncontrados([]))
        .finally(() => vigente && setBuscando(false));
    }, 500);
    return () => {
      vigente = false;
      clearTimeout(t);
    };
  }, [busqueda, storeId, renglon.nombre]);

  const candidatosVisibles = encontrados ?? renglon.candidatos;

  return (
    <>
      <tr className={`border-b hover:bg-muted/30 ${renglon.ignorado ? 'opacity-50' : ''}`}>
        <td className="p-2 align-top">
          <button
            onClick={() => setAbierta((v) => !v)}
            className="flex items-start gap-1 text-left w-full"
          >
            {abierta ? (
              <ChevronDown className="w-3 h-3 mt-1 shrink-0" />
            ) : (
              <ChevronRight className="w-3 h-3 mt-1 shrink-0" />
            )}
            <span className="text-sm">{renglon.nombre}</span>
          </button>
          {(renglon.marca || renglon.categoria) && (
            <span className="text-xs text-muted-foreground ml-4">
              {[renglon.marca, renglon.categoria].filter(Boolean).join(' · ')}
            </span>
          )}
        </td>

        <td className="p-2 text-sm text-right align-top whitespace-nowrap">
          Bs {renglon.precio.toLocaleString('es-BO')}
        </td>

        {/*
          La columna de variación (§4.3). Si un renglón se reconoce con 91 %
          pero el precio salta de Bs 89 a Bs 2.400, no es un error de identidad
          sino de LECTURA. Sin esto entraba con alta confianza y envenenaba la
          comparación.
        */}
        <td className="p-2 text-sm text-right align-top whitespace-nowrap">
          {renglon.variacion == null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span
              className={
                Math.abs(renglon.variacion) >= VARIACION_SOSPECHOSA
                  ? 'text-red-600 font-medium'
                  : 'text-muted-foreground'
              }
              title={
                renglon.precioAnterior != null
                  ? `Antes: Bs ${renglon.precioAnterior.toLocaleString('es-BO')}`
                  : undefined
              }
            >
              {formatoPorcentaje(renglon.variacion, { signo: true, decimales: 0 })}
              {Math.abs(renglon.variacion) >= VARIACION_SOSPECHOSA && (
                <TrendingUp className="w-3 h-3 inline ml-1" />
              )}
            </span>
          )}
        </td>

        <td className="p-2 text-sm align-top">
          {mejor ? (
            <span>
              {mejor.nombre}{' '}
              <span className="text-muted-foreground">
                ({formatoPorcentaje(mejor.confianza, { decimales: 0 })})
              </span>
              {/*
                🔴 Un candidato que salió de una carga manual anterior NO es un
                artículo que la tienda mostró. Sin decirlo, el panel parecía
                haber encontrado el producto en la tienda cuando en realidad
                reconoció un renglón que alguien escribió antes.
              */}
              {mejor.deCargaManual && (
                <span className="block text-xs text-yellow-600">
                  De una carga manual anterior, no del catálogo de {nombreTienda}
                </span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">sin candidatos</span>
          )}
        </td>

        <td className="p-2 align-top">
          <Badge className={estado.clase} variant="secondary">
            {estado.texto}
          </Badge>
          {renglon.ignorado && (
            <span className="text-xs text-muted-foreground ml-1">(ignorado)</span>
          )}
          {renglon.motivoRechazo && (
            <p className="text-xs text-muted-foreground mt-0.5 max-w-xs">{renglon.motivoRechazo}</p>
          )}
          {/*
            🔴 Reconocido en la tienda pero sin llegar a un producto nuestro: el
            precio NO se ve en Productos, y la fila tiene que decirlo. Sin esto
            decía «Reconocido», no pasaba nada, y nadie entendía por qué.
          */}
          {renglon.estado === 'RECONOCIDO' && !renglon.vinculadoAProducto && (
            <p className="text-xs text-yellow-600 mt-0.5 max-w-xs">
              Falta vincularlo con un producto nuestro: el precio todavía no se ve en Productos.
            </p>
          )}
        </td>

        <td className="p-2 text-right align-top whitespace-nowrap">
          {mejor && !yaDecidido && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={() => onDecidir({ tipo: 'CONFIRMAR', externalIdElegido: mejor.externalId })}
            >
              <Check className="w-3 h-3 mr-1" />
              Es éste
            </Button>
          )}
          {!yaDecidido && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={() => onDecidir({ tipo: 'MARCAR_NUEVO' })}
            >
              <Plus className="w-3 h-3 mr-1" />
              Es nuevo
            </Button>
          )}
          {!renglon.ignorado && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7"
              title="Ignorar este renglón"
              onClick={() => onDecidir({ tipo: 'IGNORAR' })}
            >
              <EyeOff className="w-3 h-3" />
            </Button>
          )}
        </td>
      </tr>

      {abierta && (
        <tr className="bg-muted/20">
          <td colSpan={6} className="p-3">
            {/*
              🔴 §4.1: el buscador está acotado a esta tienda y lo dice en
              pantalla. Si «Monitor Samsung 24"» de Dismac se vinculara contra
              un artículo de Multicenter, se rompería la serie de precios de las
              dos.
            */}
            <Input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder={`Buscar entre los artículos de ${nombreTienda}…`}
              className="h-8 mb-2"
            />
            <p className="text-xs text-muted-foreground mb-2">
              Sólo artículos de {nombreTienda}. Nunca se mezclan tiendas.
              {encontrados && ' Podés elegir cualquiera, sin importar el porcentaje.'}
            </p>

            {buscando ? (
              <p className="text-sm text-muted-foreground py-2">Buscando en {nombreTienda}…</p>
            ) : candidatosVisibles.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                {encontrados
                  ? `Ningún artículo de ${nombreTienda} coincide con esa búsqueda.`
                  : `Ningún artículo de ${nombreTienda} se parece a este renglón. Buscalo por su nombre acá arriba, o marcalo como producto nuevo.`}
              </p>
            ) : (
              <ul className="space-y-1">
                {candidatosVisibles.map((c) => (
                  <li key={c.externalId} className="flex items-center justify-between gap-2">
                    <span className="text-sm">
                      {c.nombre}{' '}
                      <span className="text-muted-foreground">
                        ({formatoPorcentaje(c.confianza, { decimales: 0 })})
                      </span>
                      {c.deCargaManual && (
                        <span className="text-xs text-yellow-600"> · de una carga manual</span>
                      )}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 shrink-0"
                      onClick={() =>
                        onDecidir({ tipo: 'CONFIRMAR', externalIdElegido: c.externalId })
                      }
                    >
                      Es éste
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
